/**
 * The cycle workspace for a caller WITHOUT workflow access (f11.3, Q-f11.3-2).
 *
 * The same page head as f10.3's workspace -- breadcrumb, label, subtitle and
 * state badge -- with no state action and no preparation step, then the
 * Forecast input card or the "Nothing to input" state. It reads only
 * `input-entry`: never `…/workflow` and never `GET /cycles/{id}`, both of
 * which stay Finance/Admin (BR-F11.3-6).
 *
 * f16.2: "Open review" renders from the server's `review_access`, so a DM or
 * an Executive reaches the review screen exactly when it will answer.
 */

import { Link } from 'react-router-dom';
import { Inbox } from 'lucide-react';

import type { CycleInputEntry } from '@/api/client';
import { ExportCycleCsvButton } from '@/features/cycle-export/ExportCycleCsvButton';

import { CycleStateBadge } from './CycleStateBadge';
import { ForecastInputCard } from './ForecastInputCard';
import { ReviewLink } from './review/ReviewLink';
import { inputEntrySubtitle, nothingToInput } from './inputEntryView';

export function NothingToInput({ code }: { code: string }) {
  const copy = nothingToInput(code);
  return (
    <div className="card">
      <div className="empty" data-testid="nothing-to-input">
        <div className="e-icon">
          <Inbox aria-hidden="true" />
        </div>
        <h3>{copy.title}</h3>
        <p>{copy.body}</p>
      </div>
    </div>
  );
}

export function ScopedCycleWorkspace({ input }: { input: CycleInputEntry }) {
  const { cycle, entry } = input;
  return (
    <>
      <div className="page-head">
        <div>
          <div className="header-crumb">
            <Link to="/cycles">Cycles</Link> <span>/</span> <b>{cycle.code}</b>
          </div>
          <h1 className="page-title">{cycle.label}</h1>
          <div className="page-sub">{inputEntrySubtitle(input)}</div>
        </div>
        <div className="page-actions">
          <CycleStateBadge state={cycle.state} />
          {input.review_access && <ReviewLink cycleId={cycle.id} label="Open review" />}
          <ExportCycleCsvButton cycleId={cycle.id} stateCode={cycle.state.code} />
        </div>
      </div>

      <div className="cycle-scoped-rail">
        {entry.shown ? <ForecastInputCard input={input} /> : <NothingToInput code={cycle.code} />}
      </div>
    </>
  );
}
